import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import GoogleMap from "@/components/GoogleMap";
import NearbySuburbs from "@/components/NearbySuburbs";
import RelatedServiceLinks from "@/components/RelatedServiceLinks";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { locationConfigs } from "@/data/locationData";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    MapPin,
    CheckCircle,
    Users,
    Heart,
    Shield,
    Star,
    Award,
    Baby
} from "lucide-react";

const meadowHeightsFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'How far is Y3 Smiles Dental from Meadow Heights?',
      acceptedAnswer: { '@type': 'Answer', text: 'About 5\u20137 minutes\u2019 drive south along Pascoe Vale Road. We are at Unit 1, 34 King William Street, Broadmeadows VIC 3047.' } },
    { '@type': 'Question', name: 'Do you offer same-day emergency dental for Meadow Heights patients?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. We reserve emergency slots in our daily schedule for severe pain, broken teeth, knocked-out teeth, swelling and trauma. Call (03) 9022 4442 and we will do our best to see you the same day.' } },
    { '@type': 'Question', name: 'Do you bulk-bill children\u2019s dental through CDBS?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. Eligible children aged 0\u201317 can receive up to $1,158 in dental services every two calendar years under the Medicare Child Dental Benefits Schedule. We check eligibility, bulk-bill the benefit, and there is no out-of-pocket cost for covered treatments.' } },
    { '@type': 'Question', name: 'Do you have a Turkish-speaking dentist?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes \u2014 our principal dentist Dr Fatima Kurnaz is bilingual in English and Turkish.' } },
    { '@type': 'Question', name: 'Do you have an Arabic-speaking team member?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. Our reception and clinical team includes Arabic-speaking staff. Mention your preferred language when booking.' } },
    { '@type': 'Question', name: 'What health funds do you accept?',
      acceptedAnswer: { '@type': 'Answer', text: 'All major Australian health funds with HICAPS instant claiming on-site, including Bupa, Medibank Private, HCF, NIB (we are a NIB First Choice provider), CBHS, AHM and others.' } },
    { '@type': 'Question', name: 'What are your opening hours?',
      acceptedAnswer: { '@type': 'Answer', text: 'Monday to Friday 9:00 AM \u2013 5:30 PM, Saturday 9:00 AM \u2013 1:00 PM, closed Sunday.' } },
    { '@type': 'Question', name: 'Is parking free?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes, free parking is available directly in front of the clinic on King William Street.' } },
    { '@type': 'Question', name: 'How do I get to the clinic from Meadow Heights by public transport?',
      acceptedAnswer: { '@type': 'Answer', text: 'Take the 540 bus to Broadmeadows Central (about 12 minutes), then walk along King Street to the clinic (about 5 minutes).' } },
    { '@type': 'Question', name: 'Do you offer dental implants?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. Single-tooth implants from $4,000 all-inclusive; full-arch (All-on-X) from $24,500 per arch. Dr Pavel provides a free initial consultation with a written quote.' } },
    { '@type': 'Question', name: 'What payment plans are available?',
      acceptedAnswer: { '@type': 'Answer', text: 'Interest-free payment plans up to 12 months for treatments over $300, and extended low-interest plans up to 24 months. We accept EFTPOS, Visa, Mastercard. ATO Compassionate Release of Superannuation may be available for clinically-required treatment.' } },
    { '@type': 'Question', name: 'What ages do you treat?',
      acceptedAnswer: { '@type': 'Answer', text: 'We treat patients of all ages \u2014 from a child\u2019s first tooth (around age 1) through to elderly patients. CDBS covers eligible children 0\u201317.' } },
  ],
};


const MeadowHeightsPage = () => {
    useScrollAnimation();

    const services = [
        "Family Dentistry: Fillings, dentures, extractions, root canal treatments",
        "Children's Dentistry: Fluoride applications, oral hygiene education, fissure sealants",
        "Cosmetic Dentistry: Veneers, teeth whitening, crowns, reshaping teeth",
        "Gentle Care Options: Designed for anxious or nervous patients",
        "Emergency Dentistry: Prompt care for unexpected pain and injuries",
        "Preventive Care: Regular check-ups and professional cleanings"
    ];

    const whyChooseUs = [
        {
            icon: Users,
            title: "Personalised Care",
            description: "Our dentists take time to listen, review your history, and tailor treatment to your needs."
        },
        {
            icon: Heart,
            title: "Comfortable Environment",
            description: "A calming clinic and caring team ensure your visits are stress-free."
        },
        {
            icon: Shield,
            title: "Comprehensive Services",
            description: "From check-ups and preventative care to cosmetic dentistry and gentle treatment options."
        },
        {
            icon: Award,
            title: "Experienced Team",
            description: "Our skilled dentists stay updated with the latest techniques and technology."
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
            <SEO
              title="Dentist Meadow Heights | Y3 Smiles Dental &#8212; Same-Day Emergency"
              description="Family dentist a short drive from Meadow Heights. Same-day emergency dental, CDBS bulk-billed kids, Turkish &amp; Arabic-speaking team. Call (03) 9022 4442."
              canonicalUrl="https://y3smilesdental.com.au/locations/meadow-heights/"
            >
              <script type="application/ld+json">{JSON.stringify(meadowHeightsFaqSchema)}</script>
            </SEO>
            <Header />
            <main className="flex-grow">
                {/* Enhanced Hero Section */}
                <section className="relative bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-32 overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary"></div>
                        <div className="absolute top-40 right-20 w-20 h-20 rounded-full bg-secondary"></div>
                        <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-primary"></div>
                        <div className="absolute bottom-32 right-1/3 w-24 h-24 rounded-full bg-secondary"></div>
                    </div>

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center justify-center mb-8 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft">
                                    <img src="/location.png" alt="Location" className="w-10 h-10 mr-4" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-primary uppercase tracking-wider">Dentist near Meadow Heights</p>
                                        <p className="text-lg font-bold text-neutral-800">Meadow Heights</p>
                                    </div>
                                </div>

                                <h1 className="text-6xl md:text-7xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Dentist Serving{" "}
                                    <span className="block text-primary">Meadow Heights</span>
                                </h1>

                                <p className="text-xl text-neutral-800 mb-12 leading-relaxed max-w-3xl mx-auto">
                                    Y3 Smiles Dental looks after Meadow Heights families from our nearby Broadmeadows clinic &#8212; a quick five-to-seven-minute drive via Pascoe Vale Road. We hold same-day emergency slots, bulk-bill eligible children through CDBS, and provide a warm, multilingual environment with Turkish-speaking and Arabic-speaking team members. Call (03) 9022 4442 or book online.
                                </p>
                            </div>

                            {/* Enhanced Feature Pills */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <CheckCircle className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">5–7 Min from Meadow Heights</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Award className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Expert Dental Team</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Heart className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Family Friendly Care</span>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="booking" size="xl" asChild>
                                    <Link to="/treatments">
                                        View Our Treatments
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced About Section */}
                <section className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-20"></div>

                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                            About Us
                                        </h2>
                                        <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 leading-tight">
                                            About Y3 Smiles Dental &#8212;
                                            <span className="block text-primary">Your Dentist near Meadow Heights</span>
                                        </h2>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-white p-6 rounded-2xl shadow-soft border-l-4 border-primary">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Y3 Smiles Dental is a modern family clinic in nearby Broadmeadows, founded by Dr Fatima Kurnaz, DDS (University of Melbourne). For Meadow Heights residents, we are the closest full-service dental practice &#8212; about five to seven minutes&#8217; drive south along Pascoe Vale Road or via Mickleham Road.
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-2xl">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                The clinic is open six days a week, including Saturday mornings, so families with school-age children or shift workers can find a convenient time without missing work.
                                            </p>
                                        </div>

                                        <div className="bg-primary/10 p-6 rounded-2xl border border-primary/20">
                                            <p className="text-lg text-primary font-semibold leading-relaxed">
                                                We offer transparent quotes before any treatment begins and a comfortable environment for nervous patients and children alike.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Button variant="booking" size="lg" asChild className="group">
                                            <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                                <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                                BOOK CONSULTATION
                                            </a>
                                        </Button>
                                        <Button variant="booking" size="lg" asChild>
                                            <Link to="#services">Learn More About Our Services</Link>
                                        </Button>
                                    </div>
                                </div>

                                <div className="relative">
                                    {/* Decorative Frame */}
                                    <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl"></div>
                                    <div className="relative bg-white p-2 rounded-3xl shadow-2xl">
                                        <div className="aspect-[4/3] bg-neutral-100 rounded-2xl overflow-hidden">
                                            <img
                                                src="/meadow-heights.png"
                                                alt="Modern dental office in Meadow Heights"
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Floating Stats */}
                                    {/* <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-primary/10">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-primary"></div>
                                            <div className="text-sm text-neutral-800 font-medium">Happy Patients</div>
                                        </div>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced Why Choose Us Section */}
                <section className="py-32 bg-gradient-to-br from-neutral-50 to-primary/5 relative overflow-hidden">

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-20">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Excellence in Care
                                </h2>

                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Dental Services Available
                                    <span className="block text-primary">
                                        to Meadow Heights Families
                                    </span>
                                </h2>

                                <p className="text-xl text-neutral-800 max-w-4xl mx-auto leading-relaxed">
                                    Meadow Heights patients receive the full clinical range at Y3 Smiles &#8212; from general family dentistry and CDBS bulk billing for kids through to cosmetic treatments, implants and same-day emergency care.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                                {whyChooseUs.map((item, index) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <div key={index} className="group bg-white p-8 rounded-3xl shadow-soft hover:shadow-2xl transition-all duration-500 border border-primary/5 hover:border-primary/20 relative overflow-hidden">
                                            {/* Background Gradient */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-secondary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-500"></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-6">
                                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 text-primary flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 shadow-soft">
                                                        <IconComponent className="w-8 h-8" />
                                                    </div>
                                                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full group-hover:w-16 transition-all duration-300"></div>
                                                </div>

                                                <h3 className="text-xl font-bold text-neutral-800 mb-4 group-hover:text-primary transition-colors duration-300">
                                                    {item.title}
                                                </h3>
                                                <p className="text-neutral-800 leading-relaxed text-lg">
                                                    {item.description}
                                                </p>
                                            </div>

                                            {/* Decorative Element */}
                                            <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full group-hover:scale-125 transition-transform duration-500 opacity-50"></div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Call to Action */}
                            <div className="text-center mt-16">
                                <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
                                    <Button variant="booking" size="xl" asChild className="group">
                                        <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                            <Heart className="w-5 h-5 mr-2 group-hover:scale-125 transition-transform" />
                                            EXPERIENCE THE DIFFERENCE
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Enhanced Services Section */}
                <section id="services" className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-20">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Our Treatments
                                </h2>

                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Our Dental Services in
                                    <span className="block text-primary">Meadow Heights</span>
                                </h2>

                                <p className="text-xl text-neutral-800 mb-8 max-w-4xl mx-auto leading-relaxed">
                                    From routine check-ups to advanced treatments, we provide comprehensive dental
                                    care for every member of your family in a comfortable, modern environment.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                                {services.map((service, index) => {
                                    const [title, description] = service.split(': ');
                                    return (
                                        <div key={index} className="group bg-white p-8 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 relative overflow-hidden">
                                            {/* Background Effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-secondary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-300"></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-6">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                                                        <CheckCircle className="w-6 h-6 text-primary" />
                                                    </div>
                                                    <div className="w-8 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                                                </div>

                                                <h3 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors duration-300">
                                                    {title}
                                                </h3>

                                                <p className="text-neutral-800 leading-relaxed">
                                                    {description}
                                                </p>
                                            </div>

                                            {/* Hover Effect */}
                                            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Enhanced CTA Section */}
                            <div className="bg-gradient-to-r from-primary/10 via-white to-secondary/10 p-12 rounded-3xl border border-primary/20 text-center">
                                <h3 className="text-3xl font-bold text-neutral-800 mb-4">
                                    Ready to Transform Your Smile?
                                </h3>
                                <p className="text-lg text-neutral-800 mb-8 max-w-2xl mx-auto">
                                    Discover our complete range of dental treatments designed to give you
                                    the healthy, beautiful smile you deserve.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                    <Button variant="booking" size="xl" asChild className="group">
                                        <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                            <Star className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                            BOOK YOUR CONSULTATION
                                        </a>
                                    </Button>
                                    <Button variant="booking" size="xl" asChild>
                                        <Link to="/treatments">
                                            <Shield className="w-5 h-5 mr-2" />
                                            VIEW ALL TREATMENTS
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Children's Dentistry & CDBS Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Children&#8217;s Dentistry and CDBS Bulk Billing for Meadow Heights Families
                            </h2>
                            <div className="bg-primary/5 rounded-2xl p-8 border border-primary/10 mb-8">
                                <p className="text-lg text-neutral-700 leading-relaxed mb-4">
                                    The Medicare Child Dental Benefits Schedule (CDBS) provides up to <strong>$1,158 of dental services every two calendar years</strong> for eligible children aged 0&#8211;17. Y3 Smiles is an approved CDBS provider and we bulk-bill the benefit, so families pay nothing out of pocket for covered treatments.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed mb-4">
                                    Many Meadow Heights families bring their kids for the first time at age 1&#8211;2 &#8212; a low-pressure introduction to the chair, a quick gentle examination, and a fluoride application. We use language children can understand and never rush an anxious first visit.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    For nervous kids we offer a slow first visit, happy gas (nitrous oxide), and a friendly explanation of every step. Our goal is for every child to leave feeling good about the dentist.
                                </p>
                            </div>
                            <div className="text-center">
                                <Button variant="booking" size="lg" asChild>
                                    <Link to="/treatments/childrens-dentistry">
                                        <Baby className="w-5 h-5 mr-2" />
                                        Learn About Children&#8217;s Dentistry
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Emergency Dental Section */}
                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Same-Day Emergency Dental for Meadow Heights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                <div className="bg-white rounded-2xl p-6 shadow-soft border-l-4 border-primary">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-3">We treat dental emergencies including:</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Severe toothache</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Knocked-out or broken tooth</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Broken filling or crown</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Dental abscess and facial swelling</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Trauma after a fall or sports injury</span></li>
                                    </ul>
                                </div>
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-3">Before you arrive:</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Rinse with warm salt water</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Apply a cold compress for swelling</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Take over-the-counter pain relief if not contraindicated</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>If a tooth is knocked out, store it in milk and bring it immediately</span></li>
                                    </ul>
                                </div>
                            </div>
                            <div className="bg-primary/10 rounded-2xl p-6 text-center border border-primary/20">
                                <p className="text-lg text-neutral-700 mb-4">The drive from Meadow Heights is short &#8212; 5&#8211;7 minutes via Pascoe Vale Road. We hold reserved emergency slots daily.</p>
                                <a href="tel:0390224442" className="inline-flex items-center justify-center gap-2 bg-primary text-white font-bold text-xl px-8 py-4 rounded-2xl hover:bg-primary/90 transition-colors">
                                    &#128222; (03) 9022 4442
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Cosmetic & Restorative Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Cosmetic and Restorative Dentistry
                            </h2>
                            <p className="text-lg text-neutral-700 leading-relaxed mb-6 text-center">
                                Whether you want to brighten your smile before a special occasion or restore a damaged tooth, every cosmetic plan starts with photographs, a written quote, and a no-obligation discussion before treatment begins.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Cosmetic Treatments</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>In-chair Zoom whitening</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Custom take-home whitening trays</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Porcelain veneers</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Dental bonding for chips and gaps</span></li>
                                    </ul>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Restorative Treatments</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Ceramic crowns and dental bridges</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Root canal therapy</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Dentures</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Full-mouth rehabilitations</span></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Dental Implants Section */}
                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Dental Implants for Meadow Heights Patients
                            </h2>
                            <div className="bg-white rounded-2xl p-8 shadow-soft mb-6">
                                <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                                    Missing teeth affect chewing, jawbone health, and facial structure. Y3 Smiles offers permanent tooth replacement solutions for Meadow Heights patients:
                                </p>
                                <div className="grid md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $4,000</div>
                                        <div className="text-sm text-neutral-700">Single-tooth implant (all-inclusive)</div>
                                    </div>
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $8,000</div>
                                        <div className="text-sm text-neutral-700">Implant-Retained Denture</div>
                                    </div>
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $24,500</div>
                                        <div className="text-sm text-neutral-700">All-on-X fixed bridge (per arch)</div>
                                    </div>
                                </div>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    Dr Pavel &#8212; 26+ years of clinical experience &#8212; provides a <strong>free initial implant consultation</strong> including a clear written quote, with payment plans from approximately $95 per week.
                                </p>
                            </div>
                            <div className="text-center">
                                <Button variant="booking" size="lg" asChild>
                                    <Link to="/dental-implants-melbourne">
                                        <Star className="w-5 h-5 mr-2" />
                                        Book a Free Implant Consultation
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Languages Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4 text-center">
                                Languages Spoken at Our Clinic
                            </h2>
                            <p className="text-lg text-neutral-700 leading-relaxed mb-8 text-center">
                                Meadow Heights is a culturally rich community and our team reflects that. Just mention your preferred language when booking.
                            </p>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">&#127462;&#127482;</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">English</h3>
                                    <p className="text-neutral-700 text-sm">All clinical and reception staff</p>
                                </div>
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">&#127481;&#127479;</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">T&#252;rk&#231;e (Turkish)</h3>
                                    <p className="text-neutral-700 text-sm">Dr Fatima Kurnaz &#8212; bilingual principal dentist</p>
                                </div>
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">&#9790;&#65039;</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">&#1575;&#1604;&#1593;&#1585;&#1576;&#1610;&#1577; (Arabic)</h3>
                                    <p className="text-neutral-700 text-sm">Arabic-speaking reception and clinical team members</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Health Funds & Payment Plans Section */}
                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-8 text-center">
                                Health Funds, Payment Plans and Transparent Pricing
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Health Funds We Accept</h3>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {["Bupa", "Medibank Private", "HCF", "NIB", "CBHS", "AHM", "HBF", "Defence Health", "Australian Unity"].map((fund) => (
                                            <span key={fund} className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">{fund}</span>
                                        ))}
                                    </div>
                                    <p className="text-sm text-neutral-700">HICAPS instant claiming on-site. NIB First Choice Network provider.</p>
                                </div>
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Payment Plans &amp; Options</h3>
                                    <ul className="space-y-3 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Interest-free plans up to 12 months (treatments over $300)</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Extended low-interest plans up to 24 months</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>EFTPOS, Visa and Mastercard accepted</span></li>
                                    </ul>
                                    <p className="text-sm text-primary font-semibold mt-4">Every patient receives a written quote before treatment proceeds.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Meet Our Team Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-5xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-10 text-center">
                                Meet Our Team
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Fatima Kurnaz</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Principal Dentist &amp; Founder</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">DDS (University of Melbourne). AHPRA DEN0001925511. Bilingual Turkish/English. 11+ years experience. Special interests: cosmetic, emergency and implant dentistry.</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Pinar Geyik</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Associate Dentist</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">BDS. AHPRA DEN0002905804. Calm, patient-centred care. Special interests: preventive, cosmetic and minor oral surgery.</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Javed Mahmud (Dr Pavel)</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Senior Dentist</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">AHPRA DEN0001774450. 26+ years experience. Special interests: implants, oral surgery and restorative dentistry.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why Choose Us Section */}
                <section className="py-20 bg-gradient-to-br from-neutral-50 to-primary/5 relative overflow-hidden">
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-10 text-center">
                                Why Meadow Heights Families Choose Y3 Smiles Dental
                            </h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                {[
                                    { icon: MapPin, title: "5\u20137 minute drive", desc: "Via Pascoe Vale Road \u2014 the closest full-service clinic to Meadow Heights." },
                                    { icon: Users, title: "Turkish and Arabic-speaking team", desc: "Clinical and reception team members who speak your language." },
                                    { icon: Baby, title: "CDBS bulk billing", desc: "No out-of-pocket cost for eligible children 0\u201317 under Medicare CDBS." },
                                    { icon: CheckCircle, title: "Same-day emergency slots", desc: "Reserved daily for urgent dental cases." },
                                    { icon: Award, title: "Saturday morning hours", desc: "Convenient for working families and school-age children." },
                                    { icon: Shield, title: "Free on-site parking", desc: "Directly in front of the clinic on King William Street." },
                                ].map((item, idx) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <div key={idx} className="flex items-start gap-4 bg-white p-5 rounded-2xl shadow-soft border border-primary/5">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <IconComponent className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-neutral-800 mb-1">{item.title}</h3>
                                                <p className="text-sm text-neutral-700">{item.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Getting Here Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-6 text-center">Getting to Y3 Smiles Dental from Meadow Heights</h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="bg-neutral-50 rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-primary" /> Address &amp; Parking
                                    </h3>
                                    <p className="text-neutral-700 mb-2">Unit 1, 34 King William Street, Broadmeadows VIC 3047</p>
                                    <p className="text-neutral-700 mb-4">Free on-site parking directly in front of the clinic.</p>
                                    <p className="text-neutral-700">
                                        <strong>5&#8211;7 minutes&#8217; drive</strong> from Meadow Heights &#8212; head south on Pascoe Vale Road, turn into King William Street. The clinic is on your right with parking out front.
                                    </p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4">Public Transport</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li>&#x1F68C; <strong>540 bus</strong> to Broadmeadows Central &#8212; about 12 minutes</li>
                                        <li>&#x1F6B6; 5-minute walk along King Street to the clinic</li>
                                        <li>&#x1F697; Easy access via Pascoe Vale Road and Mickleham Road</li>
                                    </ul>
                                    <div className="mt-4">
                                        <a
                                            href="https://maps.app.goo.gl/XCeiAYziTDqGdJpc8"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            Get Directions on Google Maps &#8594;
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Local Landmarks Section */}
                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-6 text-center">Local Landmarks Near Our Clinic</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-primary/10">
                                    <span className="text-2xl">&#x1F6D2;</span>
                                    <div>
                                        <p className="font-semibold text-neutral-800">Broadmeadows Central</p>
                                        <p className="text-sm text-neutral-700">5 minutes&#8217; walk</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-primary/10">
                                    <span className="text-2xl">&#x1F3DB;&#xFE0F;</span>
                                    <div>
                                        <p className="font-semibold text-neutral-800">Hume Civic Centre</p>
                                        <p className="text-sm text-neutral-700">Council offices &#8212; 4 minutes&#8217; walk</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-primary/10">
                                    <span className="text-2xl">&#x1F686;</span>
                                    <div>
                                        <p className="font-semibold text-neutral-800">Broadmeadows Train Station</p>
                                        <p className="text-sm text-neutral-700">5 minutes&#8217; walk</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-primary/10">
                                    <span className="text-2xl">&#x1F6E3;&#xFE0F;</span>
                                    <div>
                                        <p className="font-semibold text-neutral-800">Hume Freeway (M31)</p>
                                        <p className="text-sm text-neutral-700">Direct Broadmeadows exit &#8212; minutes from the clinic</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Patient Testimonials Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-8 text-center">What Our Patients Say</h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="text-primary text-3xl mb-3">&#8220;</div>
                                    <p className="text-neutral-700 leading-relaxed mb-4 italic">We&#8217;ve been bringing the whole family from Meadow Heights for two years now &#8212; the kids actually look forward to their CDBS check-ups.</p>
                                    <p className="text-sm font-semibold text-primary">&#8212; Meadow Heights patient</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="text-primary text-3xl mb-3">&#8220;</div>
                                    <p className="text-neutral-700 leading-relaxed mb-4 italic">The Turkish-speaking dentist made all the difference for my mum. She&#8217;s far more relaxed at appointments now.</p>
                                    <p className="text-sm font-semibold text-primary">&#8212; Meadow Heights patient</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="text-primary text-3xl mb-3">&#8220;</div>
                                    <p className="text-neutral-700 leading-relaxed mb-4 italic">Booked an emergency for my husband&#8217;s broken filling &#8212; they had us in within an hour. Quick, kind, and the price was exactly what they quoted.</p>
                                    <p className="text-sm font-semibold text-primary">&#8212; Meadow Heights patient</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-8 text-center">Frequently Asked Questions &#8212; Meadow Heights</h2>
                            <div className="space-y-4">
                                {[
                                    { q: "How far is Y3 Smiles Dental from Meadow Heights?", a: "About 5\u20137 minutes\u2019 drive south along Pascoe Vale Road. We are at Unit 1, 34 King William Street, Broadmeadows VIC 3047." },
                                    { q: "Do you offer same-day emergency dental for Meadow Heights patients?", a: "Yes. We reserve emergency slots in our daily schedule for severe pain, broken teeth, knocked-out teeth, swelling and trauma. Call (03) 9022 4442 and we will do our best to see you the same day." },
                                    { q: "Do you bulk-bill children\u2019s dental through CDBS?", a: "Yes. Eligible children aged 0\u201317 can receive up to $1,158 in dental services every two calendar years under the Medicare Child Dental Benefits Schedule. We check eligibility, bulk-bill the benefit, and there is no out-of-pocket cost for covered treatments." },
                                    { q: "Do you have a Turkish-speaking dentist?", a: "Yes \u2014 our principal dentist Dr Fatima Kurnaz is bilingual in English and Turkish." },
                                    { q: "Do you have an Arabic-speaking team member?", a: "Yes. Our reception and clinical team includes Arabic-speaking staff. Mention your preferred language when booking." },
                                    { q: "What health funds do you accept?", a: "All major Australian health funds with HICAPS instant claiming on-site, including Bupa, Medibank Private, HCF, NIB (we are a NIB First Choice provider), CBHS, AHM and others." },
                                    { q: "What are your opening hours?", a: "Monday to Friday 9:00 AM \u2013 5:30 PM, Saturday 9:00 AM \u2013 1:00 PM, closed Sunday." },
                                    { q: "Is parking free?", a: "Yes, free parking is available directly in front of the clinic on King William Street." },
                                    { q: "How do I get to the clinic from Meadow Heights by public transport?", a: "Take the 540 bus to Broadmeadows Central (about 12 minutes), then walk along King Street to the clinic (about 5 minutes)." },
                                    { q: "Do you offer dental implants?", a: "Yes. Single-tooth implants from $4,000 all-inclusive; full-arch (All-on-X) from $24,500 per arch. Dr Pavel provides a free initial consultation with a written quote." },
                                    { q: "What payment plans are available?", a: "Interest-free payment plans up to 12 months for treatments over $300, and extended low-interest plans up to 24 months. We accept EFTPOS, Visa, Mastercard. ATO Compassionate Release of Superannuation may be available for clinically-required treatment." },
                                    { q: "What ages do you treat?", a: "We treat patients of all ages \u2014 from a child\u2019s first tooth (around age 1) through to elderly patients. CDBS covers eligible children 0\u201317." },
                                ].map((item, i) => (
                                    <div key={i} className="bg-white rounded-2xl p-6 border border-primary/10">
                                        <h3 className="text-lg font-semibold text-neutral-800 mb-2">{item.q}</h3>
                                        <p className="text-neutral-700 leading-relaxed">{item.a}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <RelatedServiceLinks suburbName="Meadow Heights" suburbSlug="meadow-heights" className="bg-neutral-50" />

                <NearbySuburbs suburbSlug="meadow-heights" />

                <GoogleMap
                    locationName="Meadow Heights"
                    locationData={locationConfigs["meadow-heights"].locationData}
                    openingHours={locationConfigs["meadow-heights"].openingHours}
                    className="bg-primary/5"
                />
            </main>
            <Footer />
        </div>
    );
};

export default MeadowHeightsPage;
